import * as React from "react";
import { IAppliedDiscount } from "../../queries/ShoppingCartAppliedDiscountsQuery";
import * as Queries from "../../queries/ShoppingCartAppliedDiscountsQuery.g";
import { useQuery } from "@shane32/graphql";

const cacheExpiration = 1000 * 60 * 60; // 1 hour
//const cacheExpirationForError = 1000 * 10; // 10 seconds

const useCartDiscountHelper = (props: { quoteGuid: string | null }) => {
  const [appliedDiscounts, setAppliedDiscounts] = React.useState<IAppliedDiscount[] | null>(null);
  const [lastFetchTime, setLastFetchTime] = React.useState<number>(0);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const queryAppliedDiscounts = useQuery(Queries.ShoppingCartAppliedDiscountsDocument, {
    variables: { quoteGuid: props.quoteGuid },
  });

  // Fetch applied discounts
  const fetchAppliedDiscounts = React.useCallback(() => {
    const now = new Date().getTime();

    // Don't fetch if we're already loading or if the cache is still valid
    if (isLoading || (now - lastFetchTime < cacheExpiration && appliedDiscounts !== null)) {
      return Promise.resolve(appliedDiscounts);
    }

    setIsLoading(true);

    return queryAppliedDiscounts
      .refetch()
      .then((data) => {
        const discounts = data.data?.v1.my?.shoppingCartAppliedDiscounts;
        if (discounts) {
          setAppliedDiscounts(discounts);
          setLastFetchTime(now);
        }
        setIsLoading(false);
        return discounts;
      })
      .catch((error) => {
        console.error("Failed to fetch applied discounts", error);
        setIsLoading(false);
        return null;
      });
  }, [appliedDiscounts, isLoading, lastFetchTime, queryAppliedDiscounts]);

  // Get the applied discounts from the cache or fetch them if needed
  const getAppliedDiscounts = React.useCallback(() => {
    const now = new Date().getTime();

    // If the cache is still valid, return the cached discounts
    if (now - lastFetchTime < cacheExpiration && appliedDiscounts !== null) {
      return appliedDiscounts;
    }

    // Otherwise, trigger a fetch but still return the current value
    if (!isLoading) {
      fetchAppliedDiscounts();
    }

    return appliedDiscounts;
  }, [appliedDiscounts, fetchAppliedDiscounts, isLoading, lastFetchTime]);

  // Clear the cache
  const clearCache = React.useCallback(() => {
    setAppliedDiscounts(null);
    setLastFetchTime(0);
  }, []);

  return {
    getAppliedDiscounts,
    fetchAppliedDiscounts,
    clearCache,
    isLoading,
  };
};

export default useCartDiscountHelper;
